import axiosInstance from "../../core/axiosConfig.ts";
import { getAuth, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';

export type CreditLedgerEntry = {
    id: number;
    type: 'EARN' | 'TRIP_BONUS' | 'STREAK' | 'REFERRAL' | 'ADJUSTMENT' | 'RESTORE' | 'REDEEM' | 'EXPIRE' | 'CLAWBACK';
    amount: number;
    remaining: number;
    orderNumber: string | null;
    note: string | null;
    grantedByName: string | null;
    relatedCustomerName: string | null;
    createdAt: string;
    expiresAt: string | null;
    expired: boolean;
};

export type CustomerCredit = {
    customerId: number;
    customerName: string;
    balance: number;
    currency: string;
    entries: CreditLedgerEntry[];
};

export class MemberService {

    async signIn(firebaseUid: string) {
        const response = await axiosInstance.post('/auth/sign-in', { firebaseUid });
        return response.data;
    }

    async createMember(payload: any) {
        const response = await axiosInstance.post(
            'admin/member/add',
            payload
        );
    }

    async createCourier(payload: any) {
        const response = await axiosInstance.post(
            'admin/courier/add',
            payload
        );
    }

    async createVendor(payload: any) {
        const response = await axiosInstance.post(
            'admin/vendor/add',
            payload
        );
    }

    /** A customer's Vello Credit balance and everything behind it. */
    async getCustomerCredit(customerId: number): Promise<CustomerCredit> {
        const response = await axiosInstance.get('admin/customer/credit', { params: { customerId } });
        return response.data;
    }

    /**
     * Give a customer credit, or take it back with a negative amount. Returns
     * the ledger as it now stands, so the caller renders what was stored
     * rather than guessing at the new balance.
     */
    async grantCustomerCredit(payload: { customerId: number; amount: number; note: string }): Promise<CustomerCredit> {
        const response = await axiosInstance.post('admin/customer/credit/grant', payload);
        return response.data;
    }

    async deleteMember(id: any) {
        const response = await axiosInstance.delete(
            'admin/member/delete', {params : {id}}
        );
    }

    async updateMember(payload: any) {
        const response = await axiosInstance.patch(
            'admin/member/update',
            payload
        );
    }

    async updateVendor(payload: any) {
        const response = await axiosInstance.patch(
            'admin/vendor/update',
            payload
        );
    }

    async changePassword(currentPassword: string, newPassword: string): Promise<void> {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user || !user.email) {
            throw new Error('User not authenticated');
        }

        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(user, credential);

        await updatePassword(user, newPassword);
    }

    async changeMemberPassword(payload: any): Promise<void> {
        const response = await axiosInstance.patch(
            'admin/member/password/change',
            payload
        );
    } 
}